import { type EntryId, parseEntryId } from "./entryId";
import { createAction, type MutationAction } from "./mutationAction";
import {
	type ActionDeps,
	actingHeaders,
	apiCall,
	plural,
	refreshEntries,
} from "./types";

/**
 * Combine several entries into one via the v1 endpoint.
 *
 *   POST /records/combine { ids: [<n>, …], matter_id, main_entry_id }
 *
 * The server INSERTs a brand-new combined row and hard-deletes every source
 * inside one transaction. `main_entry_id` (the first entry in the dialog's
 * order) drives the combined narrative, task/activity codes, and times.
 *
 * The optimistic update removes only the non-main sources so the main row
 * stays visible as a stand-in until the post-action refresh swaps in the
 * server's combined entry.
 */
export function combineAction(
	ids: EntryId[],
	matterId: string,
	mainEntryId: EntryId,
	deps: ActionDeps,
	timekeeperId?: string | null,
): MutationAction {
	const nonMainIds = new Set(ids.filter((id) => id !== mainEntryId));
	const rollback = deps.updateEntries((prev) =>
		prev.filter((e) => !nonMainIds.has(e.id)),
	);
	const n = ids.length;

	return createAction({
		execute: () =>
			apiCall(deps, {
				endpoint: "/records/combine",
				method: "POST",
				body: {
					ids: ids.map(parseEntryId),
					matter_id: matterId,
					main_entry_id: parseEntryId(mainEntryId),
				},
				headers: actingHeaders(timekeeperId),
			}),
		rollback,
		toast: {
			loading: `Combining ${n} ${plural(n)}…`,
			success: `Combined ${n} ${plural(n)}`,
			error: "Failed to combine entries",
		},
		onSuccess: () => refreshEntries(deps),
		onError: () => refreshEntries(deps),
	});
}
